import { UseMutationOptions, UseMutationResult, useMutation } from '@tanstack/react-query';
import { InstrumentValidationError } from '@unistocks/features/external-accounts/models/instrument-validation-error';
import { t } from '@unistocks/localization';
import {
  AccountProviderDto,
  AssetDto,
  AssetType,
  ExternalAccountAuthenticateCollectDto,
  InstrumentDto,
  PortfolioDto,
  ProviderAuthenticateCollectDto,
  SectionAssetDto
} from '@unistocks/proxy/axios-client';
import { externalAccountsAuthenticateCollect } from '@unistocks/proxy/axios-client/ExternalAccountsClient';
import { externalAccountsAuthenticateCollectMutationKey } from '@unistocks/proxy/axios-client/ExternalAccountsQuery';
import { addMetaToOptions } from '@unistocks/proxy/axios-client/helpers';
import { getAssetImage } from '@unistocks/shared/utils/asset-utils';
import { isDefined, isNumber } from '@unistocks/shared/utils/util';
import { GenericAbortSignal } from 'axios';
import { createURL, openURL } from 'expo-linking';
import { use } from 'react';
import { ImageSourcePropType, Platform } from 'react-native';
import { QueryMetaContext } from 'react-query-swagger';

export const isBankIdLink = (url: string): boolean => url.startsWith('bankid.');

export const redirectToBankID = (autostartToken: string, redirectPath: string) => {
  const redirectUrl = Platform.select({
    ios: createURL(redirectPath),
    android: 'null'
  });

  return openURL(
    `https://app.bankid.com/?autostarttoken=${autostartToken}&redirect=${redirectUrl}`
  );
};

export function useExternalAccountsAuthenticateCollectMutation<TContext>(
  provider: string,
  options?: Omit<
    UseMutationOptions<
      ProviderAuthenticateCollectDto,
      unknown,
      ExternalAccountAuthenticateCollectDto,
      TContext
    >,
    'mutationKey' | 'mutationFn'
  >
): UseMutationResult<
  ProviderAuthenticateCollectDto,
  unknown,
  ExternalAccountAuthenticateCollectDto & { signal?: GenericAbortSignal },
  TContext
> {
  const key = externalAccountsAuthenticateCollectMutationKey(provider);

  const metaContext = use(QueryMetaContext);
  options = addMetaToOptions(options, metaContext);

  return useMutation({
    ...options,
    mutationFn: ({
      signal,
      ...body
    }: ExternalAccountAuthenticateCollectDto & { signal?: GenericAbortSignal }) =>
      externalAccountsAuthenticateCollect(provider, body, { signal }),
    mutationKey: key
  });
}

/**
 * Find the section in the target portfolio that matches the given instrument
 * If the instrument was imported before, the section is the same as the previous import and the old import settings are used
 * @param instrument
 * @param targetPortfolio
 * @returns
 */
export const findMatchingSection = (
  instrument: InstrumentDto,
  targetPortfolio: PortfolioDto
): string => {
  let sectionId: string;
  const matchingAsset = findMatchingAssetInTargetPortfolio(instrument, targetPortfolio);

  if (matchingAsset) {
    sectionId = matchingAsset.sectionId;
  } else {
    sectionId = targetPortfolio.sections
      .map(section => {
        const length = section.assets.filter(asset => {
          switch (instrument.assetType) {
            case AssetType.Stock:
            case AssetType.Fund:
            case AssetType.CryptoCurrency:
            case AssetType.Other:
              return asset.type === instrument.assetType;

            case AssetType.Cash:
            case AssetType.SavingsAccount:
            case AssetType.Loan:
              return [AssetType.Cash, AssetType.SavingsAccount, AssetType.Loan].includes(
                asset.type
              );
          }
        }).length;

        return { id: section.id, length };
      })
      .sort((a, b) => b.length - a.length)[0].id;
  }

  return sectionId;
};

export const findMatchingAssetInTargetPortfolio = (
  instrument: InstrumentDto,
  targetPortfolio: PortfolioDto
): SectionAssetDto => {
  return targetPortfolio.sections
    .flatMap(section => section.assets)
    .find(asset => {
      const previouslySyncedInstrument = asset.syncedAssets.find(
        syncedAsset =>
          syncedAsset.id === instrument.id && syncedAsset.portfolioId === instrument.portfolioId
      );

      if (previouslySyncedInstrument) {
        return true;
      }

      if (asset.isin && asset.isin === instrument.isin) {
        return true;
      }

      if (asset.identifier === instrument.searchResult[0]?.identifier) {
        return true;
      }

      if (instrument.searchResult[0]?.normalizedTicker === asset.normalizedTicker) {
        return true;
      }
    })!;
};

export const getInstrumentValidationError = (
  instrument: InstrumentDto,
  targetPortfolio: PortfolioDto
): InstrumentValidationError | null => {
  const hasMatchingAsset = !!findMatchingAssetInTargetPortfolio(instrument, targetPortfolio);

  if (
    instrument.importSettings.isHidden ||
    instrument.importSettings.isManualAsset ||
    hasMatchingAsset
  ) {
    return null;
  }

  if (instrument.isin && instrument.searchResult.length === 0) {
    return 'Error';
  }

  if (instrument.searchResult.length > 1) {
    return 'Warning';
  }

  return null;
};

export const getSectionValidation = (
  section: InstrumentDto[],
  targetPortfolio: PortfolioDto
): InstrumentValidationError | null => {
  const sectionErrors = section
    .filter(isDefined)
    .map(instrument => getInstrumentValidationError(instrument, targetPortfolio))
    .filter(isDefined)
    .sort();

  return sectionErrors.length ? sectionErrors[0] : null;
};

export const providerEquals = (
  provider1: AccountProviderDto,
  provider2: AccountProviderDto
): boolean => {
  return (
    provider1.name === provider2.name &&
    provider1.providerIdentifier === provider2.providerIdentifier
  );
};

export const mapInstrumentToAsset = (instrument: InstrumentDto): AssetDto =>
  ({
    type: instrument.assetType,
    imageUri: instrument.imageUri,
    market: instrument.searchResult?.length
      ? instrument.searchResult[0].market
      : instrument.marketPlace,
    isHoldings:
      [AssetType.Stock, AssetType.Fund, AssetType.CryptoCurrency].includes(instrument.assetType) ||
      (instrument.assetType === AssetType.Other &&
        isNumber(instrument.quantity) &&
        instrument.quantity > 0)
  }) as AssetDto;

export const getInstrumentImage = (
  instrument: InstrumentDto,
  isDarkMode: boolean
): ImageSourcePropType | null => {
  return getAssetImage(mapInstrumentToAsset(instrument), isDarkMode);
};

export const getInstrumentSubtitle = (instrument: InstrumentDto): string => {
  let subtitle = t('::Enum:AssetType:' + instrument?.assetType);

  if (instrument?.isin || instrument?.symbol) {
    subtitle += `: ${instrument?.isin ?? instrument?.symbol}`;
  }

  return subtitle;
};
